import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SaSiblingCallouts } from "./patio-covers.in.san-antonio";
import hero from "@/assets/original/welder-aluminum.jpg";
import heroPicture from "@/assets/original/welder-aluminum.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/aluminum-patio-covers/in/san-antonio";
const TITLE = "Aluminum Patio Covers in San Antonio, TX | Texas Made Shade";
const DESC = "Custom welded aluminum patio covers for San Antonio homes & businesses. Compare fabric and solid roof options. Fabricated in Seguin. Request a free quote.";

export const Route = createFileRoute("/aluminum-patio-covers/in/san-antonio")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Aluminum fabrication in the Texas Made Shade Seguin shop",
      socialTitle: "Aluminum Patio Covers in San Antonio, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Aluminum Patio Covers in San Antonio, TX",
        breadcrumbName: "Aluminum Patio Covers in San Antonio",
        description: DESC,

      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Aluminum Patio Covers in San Antonio, TX",
          serviceType: "Aluminum Patio Cover Fabrication & Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "San Antonio", containedInPlace: { "@type": "AdministrativeArea", name: "Bexar County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Aluminum Patio Covers in San Antonio"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/san-antonio" },
        { label: "Aluminum Patio Covers in San Antonio, TX" },
      ]}
      eyebrow="★ ALUMINUM PATIO COVERS · SAN ANTONIO, TX ★"
      title="Welded Aluminum Patio Covers in San Antonio, TX"
      intro="Custom welded aluminum patio covers for San Antonio homes and businesses. We fabricate in Seguin and discuss roof material, finish, attachment and installation requirements for your property."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Aluminum Fabricator · Serving San Antonio, TX",
        headline: "Aluminum Patio Cover Quote — Free On-Site Estimate",
        subhead: "Send measurements and photos. We'll quote frame size, fabric or solid top, and color in one written estimate.",
        primaryHref: "#lets-talk",
        primaryLabel: "Get My Aluminum Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Structural-grade welded aluminum (not screw-together kits)",
        "Powder-coated in 5 standard colors + custom RAL",
        "Framing and anchorage selected for your site",
        "Attached and freestanding configurations",
        "Product-specific warranty terms in your quote",
        "Built in Seguin · installed across San Antonio",
      ]}
      body={[
        {
          heading: "Welded, Not Bolted Together",
          text: "Our Seguin shop fabricates custom aluminum frames to suit the planned cover. Dimensions, finish and connections are reviewed for your building rather than selected solely from a standard kit.",
        },
        {
          heading: "Built for South Texas Wind",
          text: "Wind requirements depend on the site, dimensions and applicable building requirements. Discuss posts, beams, footings, engineering and permit or HOA documentation before the project scope is finalized.",
          cta: { label: "Patio covers overview", href: "/patio-covers/in/san-antonio" },
        },
        {
          heading: "Pair It with the Right Top",
          text: "An aluminum frame is half the cover. Pick a Sunbrella canvas top for soft shade, Phifertex Plus stretched panels for breeze + UV block, or insulated Snap-N-Lock roof for true heat rejection and rain protection. We size and recommend on the same quote.",
          cta: { label: "See insulated cover options", href: "/insulated-patio-covers/in/san-antonio" },
        },
      ]}
      applications={[
        "Attached Patio Covers",
        "Freestanding Pavilions",
        "Carports & Side-Yard Covers",
        "Outdoor Kitchen Shade",
        "Commercial Awning Frames",
        "Replacement Pergola Frames",
      ]}
      faqs={[
        { q: "Why consider aluminum for a patio cover?", a: "Aluminum resists rust and rot and can suit custom outdoor frames. The roof, finish and structural design should be chosen for your site." },
        { q: "Is an aluminum cover hail-proof?", a: "No cover should be described as hail-proof. Storm damage depends on roof material, hail size and impact; ask about the selected product’s warranty and exclusions." },
        { q: "Can I choose the finish?", a: "Yes. Discuss the finish and color you want; availability, price and timing are confirmed in your quote." },
        { q: "What maintenance and warranty apply?", a: "Maintenance varies by roof and finish. Your quote should identify the selected materials, care requirements and applicable warranty terms." },
      ]}
      cityCallouts={[
        { to: "/san-antonio", city: "San Antonio", anchorText: "All Texas Made Shade services in San Antonio", blurb: "See every shade, awning and welding service we offer to San Antonio." },
        { to: "/custom-fabrication", city: "Our Shop", anchorText: "Inside our welding & fabrication shop", blurb: "Every aluminum patio cover is welded in our Seguin facility before it ships to San Antonio." },
      ]}
      postCta={<SaSiblingCallouts current={PATH} />}
    />
  );
}
